/**
 * Presentational metadata for printer connection interfaces — shared by the
 * Settings printer list and the Printer Helper's inline discovery list.
 */
import MuiLanIcon from '@mui/icons-material/Lan';
import MuiUsbIcon from '@mui/icons-material/Usb';
import MuiPrintIcon from '@mui/icons-material/Print';
import MuiBluetoothIcon from '@mui/icons-material/Bluetooth';

import { escPosTransportOf, type SavedPrinter, type SavedEscPosPrinter, type StarPrinterInterface } from 'src/services/printers';

import type { MuiIconComponent } from 'src/components/mui-icon';

/** Icon used for a printer connection interface. */
export function interfaceIcon(type: StarPrinterInterface): MuiIconComponent {
  switch (type) {
    case 'lan':
      return MuiLanIcon;
    case 'bluetooth':
    case 'ble':
      return MuiBluetoothIcon;
    case 'usb':
      return MuiUsbIcon;
    default:
      return MuiPrintIcon;
  }
}

/** Friendly label for a configuration issue's interface. */
export function issueLabel(interfaceType?: StarPrinterInterface): string {
  switch (interfaceType) {
    case 'lan':
      return 'Wi-Fi / LAN';
    case 'bluetooth':
      return 'Bluetooth';
    case 'ble':
      return 'Bluetooth LE';
    case 'usb':
      return 'USB';
    default:
      return 'Discovery';
  }
}

/** Operator-facing method name for a saved printer. */
export function printerMethodLabel(printer: Pick<SavedPrinter, 'driver'>): string {
  return printer.driver === 'escpos' ? 'Generic ESC/POS' : 'Star Micronics';
}

/** "Network printer", "USB printer" or "Serial printer" for a generic ESC/POS record. */
export function escPosKindLabel(printer: Pick<SavedEscPosPrinter, 'transport' | 'connection'>): string {
  switch (escPosTransportOf(printer)) {
    case 'windows-raw':
      return 'USB printer';
    case 'serial':
      return 'Serial printer';
    case 'lan':
    default:
      return 'Network printer';
  }
}

/** Where a generic ESC/POS printer is: `192.168.1.50:9100`, `POS-80 (USB001)`, `COM3 · 9600 baud`. */
export function escPosEndpointLabel(printer: Pick<SavedEscPosPrinter, 'transport' | 'connection'>): string {
  const { connection } = printer;
  switch (escPosTransportOf(printer)) {
    case 'windows-raw': {
      const raw = connection as { queueName: string; portName?: string };
      return raw.portName ? `${raw.queueName} (${raw.portName})` : raw.queueName;
    }
    case 'serial': {
      const serial = connection as { portName: string; baudRate: number };
      return `${serial.portName} · ${String(serial.baudRate)} baud`;
    }
    case 'lan':
    default: {
      const lan = connection as { ipAddress: string; port: number };
      return `${lan.ipAddress}:${String(lan.port)}`;
    }
  }
}

/** "TSP100III · BT" for Star; "ESC/POS · USB printer · POS-80 (USB001)" for a generic printer. */
export function printerSubtitle(printer: SavedPrinter): string {
  if (printer.driver === 'escpos') {
    return `ESC/POS · ${escPosKindLabel(printer)} · ${escPosEndpointLabel(printer)}`;
  }
  const { connection } = printer;
  const endpoint =
    connection.interfaceType === 'lan' && (connection.ipAddress ?? connection.address)
      ? ` · ${connection.ipAddress ?? connection.address}`
      : '';
  return `${printer.model} · ${printer.interfaceType.toUpperCase()}${endpoint}`;
}
